import React from 'react';
import { useDispatch } from 'react-redux';
import {
  getFinanceByStationMonth,
  upsertFinanceByStationMonth,
  updateFinanceById,
  uploadFinanceReport,
  downloadFinanceReport,
} from '../../modules/analytics';
import {
  Modal,
  Form,
  InputNumber,
  Input,
  Select,
  Button,
  Row,
  Col,
  message
} from 'antd';
import FinanceBlopsSchedule from './FinanceBlopsSchedule';

// const demoData = {
//   "stationId": "1299184320439002220",
//   "reportMonth": "2025-08",
//
//   "generatedKwh": 182340.0,
//   "discountKwh": 2150.0,
//
//   "grid": {
//     "kwh": 42000.0,
//     "fit": 0.32
//   },
//
//   "site": {
//     "kwh": 138000.0,
//     "priceModel": "BLOPS",
//     "fixedPrice": 0.5,
//     "blops": [
//       { "tier": "S", "unitPrice": 1.05, "usage": 12000 },
//       { "tier": "P",       "unitPrice": 0.86, "usage": 38000 },
//       { "tier": "O",   "unitPrice": 0.58, "usage": 54000 },
//       { "tier": "L",        "unitPrice": 0.42, "usage": 25000 },
//       { "tier": "B",     "unitPrice": 0.30, "usage": 9000 }
//     ],
//     "blopsSchedule": [
//       "B","B","B","L","L","O","O","O",
//       "P","P","P","S","S","P","P","O",
//       "O","O","L","L","B","B","B","B"
//     ]
//   },
//
//   "summary": {
//     "selfUtilization": 72.5,
//     "utilizationHours": 4.25
//   },
//
//   "attachments": {
//     "lineLossDocUrl": null,
//     "prDocUrl": null
//   },
//
//   "currency": "CNY",
//   "version": 1,
//   "createdAt": "2025-08-15T03:21:04.000Z",
//   "updatedAt": "2025-08-15T03:21:04.000Z"
// }

const tierStyles = {
  'super-peak': { border: '#E60F11', background: '#FFD6D6', label: 'Super Peak', labelSuffix: '尖' },
  'peak':       { border: '#FFA53C', background: '#FFE8CD', label: 'Peak',       labelSuffix: '峰' },
  'off-peak':   { border: '#50CD89', background: '#C4EBD9', label: 'Off Peak',   labelSuffix: '平' },
  'low':        { border: '#3CA8FF', background: '#CDE9FF', label: 'Low',        labelSuffix: '谷' },
  'bottom':     { border: '#AB4DCA', background: '#E69ADC', label: 'Bottom',     labelSuffix: '深谷' },
};


const ALL_BLOPS_TIERS = ['super-peak', 'peak', 'off-peak', 'low', 'bottom'];

const KEY_TO_CODE = { 'super-peak': 'S', peak: 'P', 'off-peak': 'O', low: 'L', bottom: 'B' };
const ensureYYYYMM = (iso) => {
  if (!iso) return '';
  const s = String(iso);
  if (/^\d{4}-\d{2}$/.test(s)) return s;
  const d = new Date(s);
  if (!isNaN(d)) {
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    return `${y}-${m}`;
  }
  return '';
};

// Accept both 1-letter codes and canonical keys, return canonical key or null
const CODE_TO_KEY = { S: 'super-peak', P: 'peak', O: 'off-peak', L: 'low', B: 'bottom' };
const KEY_ALIASES = {
  'super-peak': 'super-peak', 'super peak': 'super-peak', 'superpeak': 'super-peak',
  'peak': 'peak',
  'off-peak': 'off-peak', 'off peak': 'off-peak', 'offpeak': 'off-peak',
  'low': 'low',
  'bottom': 'bottom'
};
const toCanonicalTier = (raw) => {
  if (raw == null) return null;
  const s = String(raw).trim();
  if (!s) return null;
  const letter = s.toUpperCase();
  if (CODE_TO_KEY[letter]) return CODE_TO_KEY[letter];
  const key = s.toLowerCase().replace(/\s+/g, '-');
  return KEY_ALIASES[key] || null;
};

const getTierMeta = (key) => tierStyles[key] || { border: '#ccc', background: '#eee', label: key || 'Tier', labelSuffix: '' };

const formatNumberWithCommas = (v) => {
  if (v === undefined || v === null || v === '') return '';
  const num = Number(v);
  if (Number.isNaN(num)) return String(v);
  const s = String(v);
  if (Math.abs(num) < 1) return s; // no grouping for values between -1 and 1
  const [intPart, decPart] = s.split('.');
  const intWith = intPart.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  return decPart !== undefined ? `${intWith}.${decPart}` : intWith;
};

const formatCurrency = (value) => {
  if (value == null) return '0.00';
  const num = Number(value);
  if (Number.isNaN(num)) return '0.00';
  const fixed = num.toFixed(2);
  if (Math.abs(num) < 1) return fixed; // 0.xx or -0.xx without grouping
  const [intPart, decPart] = fixed.split('.');
  const intWith = intPart.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  return `${intWith}.${decPart}`;
};

const formatMax2 = (value) => {
  if (value == null || value === '') return '0';
  const num = Number(value);
  if (!Number.isFinite(num)) return '0';
  const rounded = Math.round(num * 100) / 100; // cap to 2 decimals
  return formatNumberWithCommas(String(rounded));
};

// Numeric conversion helper
const toNum = (v, d = 0) => {
  const n = Number(v);
  return Number.isFinite(n) ? n : d;
};

// Site capacity lookup helper
const getSiteCapacityKw = (stationInfo) => {
  const cand = [
    stationInfo?.capacityKw,
    stationInfo?.capacityKW,
    stationInfo?.capacity,
    stationInfo?.kWp,
    stationInfo?.kwp,
    stationInfo?.capacityKwp,
  ];
  const val = cand.find((x) => Number.isFinite(Number(x)));
  return toNum(val, 0);
};

const FinanceEditModal = ({
  isOpen,
  editingRow,
  inspectionTime,
  form,
  onSubmit,
  onClose,
}) => {
  // Fallback form instance if not provided
  const [internalForm] = Form.useForm();
  const formInstance = form || internalForm;

  const dispatch = useDispatch();
  const stationId = inspectionTime?.stationInfo?.stationId || '';
  // Memoize the month string from inspectionTime.date; avoid re-running effects on unrelated inspectionTime changes
  const initialMonth = React.useMemo(() => ensureYYYYMM(inspectionTime?.date) || '', [inspectionTime?.date]);

  // Hold picked files until Save; do not upload immediately
  const [pendingFiles, setPendingFiles] = React.useState({ lineLoss: null, pr: null });
  // Upload state and refs for file inputs (uploading not used for UI anymore)
  const lineLossInputRef = React.useRef(null);
  const prInputRef = React.useRef(null);

  const monthKeyCurrent = React.useMemo(() => {
    const watched = formInstance.getFieldValue('reportMonth') || '';
    return (editingRow?.period || watched || initialMonth || '').trim();
  }, [editingRow, initialMonth, formInstance]);

  const triggerPick = (kind) => {
    if (kind === 'lineLoss') lineLossInputRef.current?.click();
    if (kind === 'pr') prInputRef.current?.click();
  };

  const onPickFile = (e, kind) => {
    const file = e?.target?.files?.[0] || null;
    // reset input value so the same file can be re-picked later
    if (e?.target) e.target.value = '';
    setPendingFiles((prev) => ({ ...prev, [kind]: file }));
    if (file) {
      message.success(`${file.name} selected. It will be uploaded when you click Save.`);
    }
  };


  const handleDownload = async (kind) => {
    try {
      const monthKey = formInstance.getFieldValue('reportMonth') || ensureYYYYMM(inspectionTime?.date) || '';
      const type = kind === 'lineLoss' ? 'lineLoss' : 'pr';
      const { blob, filename } = await dispatch(downloadFinanceReport(stationId, monthKey, type));
      const suggested = filename || `${stationId}-${monthKey}-${type}.pdf`;
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = suggested;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      message.error('Download failed.');
      // eslint-disable-next-line no-console
      console.error('Download error', e);
    }
  };



  // Watch values for dynamic calculations
  const reportMonth = Form.useWatch('reportMonth', formInstance) || '';
  const generatedKwh = Form.useWatch('generatedKwh', formInstance) || 0;
  const discountKwh = Form.useWatch('discountKwh', formInstance) || 0;
  const gridKwh = Form.useWatch('gridKwh', formInstance) || 0;
  const gridFit = Form.useWatch('gridFit', formInstance) || 0;
  const siteKwh = Form.useWatch('siteKwh', formInstance) || 0;

  // Watch siteKwh for effect dependencies
  const watchedSiteKwh = Form.useWatch('siteKwh', formInstance) || 0;
  const priceModel = (Form.useWatch('priceModel', formInstance) || 'FIXED').toString().toUpperCase();
  const sitePrice = Form.useWatch('sitePrice', formInstance) || 0;
  const selfUtilization = Form.useWatch('selfUtilization', formInstance) || 0;
  const utilizationHours = Form.useWatch('utilizationHours', formInstance) || 0;
  const blops = Form.useWatch('blops', formInstance) || [];
  const blopsSchedule = Form.useWatch('blopsSchedule', formInstance) || [];
  // Watch attachment URLs in the form
  const lineLossUrl = Form.useWatch(['attachments', 'lineLossDocUrl'], formInstance) || null;
  const prUrl = Form.useWatch(['attachments', 'prDocUrl'], formInstance) || null;

  // Memoized site capacity
  const siteCapacityKw = React.useMemo(() => getSiteCapacityKw(inspectionTime?.stationInfo || {}), [inspectionTime]);

  // Auto-calculate selfUtilization, utilizationHours, and gridKwh
  React.useEffect(() => {
    const g = toNum(generatedKwh, 0);
    const site = toNum(formInstance.getFieldValue('siteKwh'), 0);
    const grid = Math.max(g - site, 0);
    const selfUtil = g > 0 ? (site / g) * 100 : 0;
    const utilHours = siteCapacityKw > 0 ? g / siteCapacityKw : 0;
    const round2 = (x) => Math.round(x * 100) / 100;
    formInstance.setFieldsValue({
      gridKwh: round2(grid),
      selfUtilization: round2(selfUtil),
      utilizationHours: round2(utilHours),
    });
  }, [generatedKwh, watchedSiteKwh, formInstance, siteCapacityKw]);

  React.useEffect(() => {
    if (!isOpen) return;

   const source = null; // no demo seed; start from inspectionTime/defaults
   const mapped = {
      reportMonth: source?.reportMonth ?? initialMonth ?? '',
      generatedKwh: source?.generatedKwh ?? 0,
      discountKwh: source?.discountKwh ?? 0,
      gridKwh: source?.grid?.kwh ?? 0,
      gridFit: source?.grid?.fit ?? 0,
      siteKwh: source?.site?.kwh ?? 0,
      priceModel: (source?.site?.priceModel ? String(source.site.priceModel).toUpperCase() : 'FIXED'),
      sitePrice: source?.site?.fixedPrice ?? 0,
      blops: Array.isArray(source?.site?.blops)
        ? source.site.blops
            .map((r) => ({ tier: toCanonicalTier(r?.tier), unitPrice: r?.unitPrice ?? 0, usage: r?.usage ?? 0 }))
            .filter((r) => r.tier)
        : [],
      blopsSchedule: Array.isArray(source?.site?.blopsSchedule)
        ? (() => {
            const canon = source.site.blopsSchedule.map((t) => toCanonicalTier(t));
            const first24 = canon.slice(0, 24);
            while (first24.length < 24) first24.push(null);
            return first24;
          })()
        : new Array(24).fill(null),
      selfUtilization: source?.summary?.selfUtilization ?? 0,
      utilizationHours: source?.summary?.utilizationHours ?? 0,
      currentBlopsTier: null,
      attachments: {
        lineLossDocUrl: null,
        prDocUrl: null,
      },
    };

    formInstance.resetFields();
    formInstance.setFieldsValue(mapped);

    // Ensure BLOPS rows in fixed order
    const list = mapped.blops || [];
    const byTier = new Map();
    list.forEach((row) => {
      const key = toCanonicalTier(row?.tier);
      if (key) byTier.set(key, { ...row, tier: key });
    });
    const merged = ALL_BLOPS_TIERS.map((t) => {
      const existing = byTier.get(t);
      return existing ? { tier: t, unitPrice: existing.unitPrice ?? 0, usage: existing.usage ?? 0 }
                      : { tier: t, unitPrice: 0, usage: 0 };
    });
    formInstance.setFieldsValue({ blops: merged });

    // Fetch from server & hydrate over demo defaults
    const monthKey = editingRow?.period || mapped.reportMonth || initialMonth || '';
    if (stationId && monthKey) {
      dispatch(getFinanceByStationMonth(stationId, monthKey))
        .then((server) => {
          if (!server) return;
          const srvMapped = {
            reportMonth: server.reportMonth || monthKey,
            generatedKwh: server.generatedKwh ?? 0,
            discountKwh: server.discountKwh ?? 0,
            gridKwh: server.grid?.kwh ?? 0,
            gridFit: server.grid?.fit ?? 0,
            siteKwh: server.site?.kwh ?? 0,
            priceModel: server.site?.priceModel ? String(server.site.priceModel).toUpperCase() : 'FIXED',
            sitePrice: server.site?.fixedPrice ?? 0,
            blops: Array.isArray(server.site?.blops)
              ? server.site.blops.map((r) => ({ tier: toCanonicalTier(r?.tier), unitPrice: r?.unitPrice ?? 0, usage: r?.usage ?? 0 }))
              : merged,
            blopsSchedule: Array.isArray(server.site?.blopsSchedule)
              ? server.site.blopsSchedule.map((t) => toCanonicalTier(t)).slice(0, 24)
              : new Array(24).fill(null),
            selfUtilization: server.summary?.selfUtilization ?? 0,
            utilizationHours: server.summary?.utilizationHours ?? 0,
            attachments: {
              lineLossDocUrl: server.attachments?.lineLossDocUrl || null,
              prDocUrl: server.attachments?.prDocUrl || null,
            },
          };
          formInstance.setFieldsValue(srvMapped);
        })
        .catch(() => {});
    }
  }, [isOpen, editingRow, formInstance, dispatch, stationId, initialMonth]);


  const handleSubmit = async () => {
    const values = await formInstance.validateFields();
    const monthKey = values.reportMonth || initialMonth || '';

    // Upload any picked files now (Save time) and collect URLs
    let lineLossDocUrl = null;
    let prDocUrl = null;
    try {
      if (pendingFiles.lineLoss) {
        const res = await dispatch(uploadFinanceReport(stationId, monthKey, pendingFiles.lineLoss, 'lineLoss'));
        lineLossDocUrl = res?.url || res?.downloadUrl || res?.path || null;
      }
      if (pendingFiles.pr) {
        const res2 = await dispatch(uploadFinanceReport(stationId, monthKey, pendingFiles.pr, 'pr'));
        prDocUrl = res2?.url || res2?.downloadUrl || res2?.path || null;
      }
    } catch (err) {
      message.error('File upload failed. Please try again.');
      // eslint-disable-next-line no-console
      console.error('Upload error', err);
      return; // stop save if uploads failed
    }

    // If no new upload, keep existing URLs from the form (so we don't overwrite with null)
    const existingLineLossUrl = formInstance.getFieldValue(['attachments', 'lineLossDocUrl']) || null;
    const existingPrUrl = formInstance.getFieldValue(['attachments', 'prDocUrl']) || null;
    if (!lineLossDocUrl) lineLossDocUrl = existingLineLossUrl;
    if (!prDocUrl) prDocUrl = existingPrUrl;

    // Normalize priceModel and blopsSchedule
    const priceModelRaw = values.priceModel || 'Fixed';
    const priceModelNormalized = String(priceModelRaw).toUpperCase(); // server expects FIXED | BLOPS

    // normalize schedule to exactly 24 items, mapping to canonical keys first
    const rawSchedule = Array.isArray(values.blopsSchedule) ? values.blopsSchedule : [];
    const canonSchedule = rawSchedule.map((t) => toCanonicalTier(t));
    while (canonSchedule.length < 24) canonSchedule.push(null);
    if (canonSchedule.length > 24) canonSchedule.length = 24;

    const payload = {
      stationId,
      reportMonth: monthKey,
      generatedKwh: Number(values.generatedKwh || 0),
      discountKwh: Math.max(0, Math.min(Number(values.discountKwh || 0), 1)),
      grid: {
        kwh: Number(values.gridKwh || 0),
        fit: Number(values.gridFit || 0),
      },
      site: {
        kwh: Number(values.siteKwh || 0),
        priceModel: priceModelNormalized,
        fixedPrice: Number(values.sitePrice || 0),
        blops: (values.blops || []).map((r) => ({
          tier: KEY_TO_CODE[toCanonicalTier(r?.tier)] || 'O',
          unitPrice: Number(r?.unitPrice || 0),
          usage: Number(r?.usage || 0),
        })),
        blopsSchedule: canonSchedule.map((t) => (t ? KEY_TO_CODE[toCanonicalTier(t)] : null)),
      },
      summary: {
        selfUtilization: Number(values.selfUtilization || 0),
        utilizationHours: Number(values.utilizationHours || 0),
      },
      ...(lineLossDocUrl || prDocUrl ? {
        attachments: {
          ...(lineLossDocUrl ? { lineLossDocUrl } : {}),
          ...(prDocUrl ? { prDocUrl } : {}),
        }
      } : {}),
    };

    try {
      if (editingRow && editingRow._id) {
        await dispatch(updateFinanceById(stationId, monthKey, editingRow._id, payload));
      } else {
        await dispatch(upsertFinanceByStationMonth(stationId, monthKey, payload));
      }
      setPendingFiles({ lineLoss: null, pr: null });
      if (typeof onSubmit === 'function') onSubmit(payload);
      if (typeof onClose === 'function') onClose();
    } catch (e) {
      // antd message error handling
      const apiErr = e && e.response && e.response.data ? e.response.data : null;
      if (apiErr && Array.isArray(apiErr.errors)) {
        const msg = apiErr.errors.map((er) => er.messages && er.messages[0]).filter(Boolean).join('\n');
        if (msg) message.error(msg);
      } else if (apiErr && apiErr.message) {
        message.error(apiErr.message);
      } else {
        message.error('Save failed. Please check the form and try again.');
      }
      // eslint-disable-next-line no-console
      console.error('Finance save failed', e);
    }
  };

  // Compute earnings from grid
  const earningsGrid = gridKwh * gridFit;

  // Compute earnings from site
  let earningsSite = 0;
  if (priceModel === 'FIXED') {
    earningsSite = siteKwh * sitePrice; // ¥/kWh * kWh
  } else if (priceModel === 'BLOPS') {
    const totalFromBlops = blops.reduce((acc, cur) => {
      const unitPrice = cur?.unitPrice || 0;
      const usage = cur?.usage || 0;
      return acc + unitPrice * usage;
    }, 0);
    const ratio = Math.max(0, Math.min(Number(discountKwh || 0), 1));
    earningsSite = totalFromBlops * ratio; // total blops ¥ * discount ratio
  }

  // Compute total earnings
  const totalEarnings = earningsGrid + earningsSite;

  // Compute total blops price for pricing table
  const totalBlopsPrice = blops.reduce((acc, cur) => {
    const unitPrice = cur?.unitPrice || 0;
    const usage = cur?.usage || 0;
    return acc + unitPrice * usage;
  }, 0);
  // Compute total blops usage for pricing table
  const totalBlopsUsage = blops.reduce((acc, cur) => acc + (cur?.usage || 0), 0);

  return (
    <Modal
      open={isOpen}
      onCancel={onClose}
      footer={null}
      width={1180}
      className="financeEditModal"
      destroyOnClose
      closable={false}
    >
      <Form form={formInstance} layout="horizontal" className="financeForm" preserve={false}>
        <div className="editGrid">
          {/* LEFT COLUMN */}
          <div className="leftCol">
            <div className="pageLabel">
              Grid Report
            </div>

            <div className="editCard">
              <div className="cardTitle">
                {inspectionTime?.stationInfo?.stationName || 'Site'}
              </div>
              <div className="siteAddress">
                {inspectionTime?.stationInfo?.addr}
              </div>

            <div className="editCardBody">
              <div className="fieldRow">
                <div className="label">Report Month:</div>
                <div className="valueRight">{reportMonth  || ''}
                <Form.Item name="reportMonth" hidden>
                  <Input />
                </Form.Item>
                </div>
              </div>
                {/* Hidden fields to register attachment URLs */}
                <Form.Item name={['attachments', 'lineLossDocUrl']} hidden>
                  <Input />
                </Form.Item>
                <Form.Item name={['attachments', 'prDocUrl']} hidden>
                  <Input />
                </Form.Item>
              <div className="fieldRow">
                <div className="label">Line Loss Value:</div>
                <Button
                  type="primary"
                  className="uploadPill"
                  onClick={() => triggerPick('lineLoss')}
                >
                  Choose File
                </Button>
                <input
                  ref={lineLossInputRef}
                  type="file"
                  accept=".pdf,image/*,.xlsx,.xls,.csv"
                  style={{ display: 'none' }}
                  onChange={(e) => onPickFile(e, 'lineLoss')}
                />
                <div className="suffix">
                  {pendingFiles.lineLoss ? (
                    <span className="fileHint">{pendingFiles.lineLoss.name}</span>
                  ) : lineLossUrl ? (
                    <a onClick={() => handleDownload('lineLoss')}>（光伏核算單）</a>
                  ) : (
                    '（光伏核算單）'
                  )}
                </div>
              </div>
              <div className="fieldRow">
                <div className="label">PR Value:</div>
                <Button
                  type="primary"
                  className="uploadPill"
                  onClick={() => triggerPick('pr')}
                >
                  Choose File
                </Button>
                <input
                  ref={prInputRef}
                  type="file"
                  accept=".pdf,image/*,.xlsx,.xls,.csv"
                  style={{ display: 'none' }}
                  onChange={(e) => onPickFile(e, 'pr')}
                />
                <div className="suffix">
                  {pendingFiles.pr ? (
                    <span className="fileHint">{pendingFiles.pr.name}</span>
                  ) : prUrl ? (
                    <a onClick={() => handleDownload('pr')}>（上網電量核算單）</a>
                  ) : (
                    '（上網電量核算單）'
                  )}
                </div>
              </div>
              <div className="fieldRow">
                <div className="label">Recorded Energy Generated:</div>
                <Form.Item name="generatedKwh" style={{ marginBottom: 0 }}>
                  <InputNumber
                    className="w100"
                    controls={false}
                    formatter={value =>
                      value !== undefined
                        ? `${value}`.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',')
                        : ''
                    }
                    parser={value =>
                      value ? value.replace(/\,/g, '') : ''
                    }
                  />
                </Form.Item>
                <div className="unit">kWh</div>
              </div>
              <div className="fieldRow">
                <div className="label">Recorded Energy Sold to Site:</div>
                <Form.Item name="siteKwh" style={{ marginBottom: 0 }}>
                  <InputNumber
                    className="w100"
                    controls={false}
                    formatter={(v) => (v !== undefined ? `${v}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',') : '')}
                    parser={(v) => (v ? v.replace(/,/g, '') : '')}
                    min={0}
                    step={1}
                  />
                </Form.Item>
                <div className="unit">kWh</div>
              </div>
            </div>
            </div>

<div className="editCard">
  <div className="cardTitle">Earnings from Grid</div>
  <div className="editCardBody">
    {/* Energy Sold to Grid */}
    <div className="fieldRow">
      <div className="label">Energy Sold to Grid:</div>
      <Form.Item name="gridKwh" style={{ marginBottom: 0 }}>
        <InputNumber
          className="w100"
          controls={false}
          readOnly
          formatter={(v) =>
            v !== undefined ? `${v}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',') : ''
          }
          parser={(v) => (v ? v.replace(/,/g, '') : '')}
        />
      </Form.Item>
      <div className="unit">kWh</div>
    </div>

    {/* Feed-in Tariff (FIT) */}
    <div className="fieldRow">
      <div className="label">Feed-in Tariff (FIT):</div>
      <Form.Item name="gridFit" style={{ marginBottom: 0 }}>
        <InputNumber className="w100" controls={false} step={0.01} min={0} />
      </Form.Item>
      <div className="unit">¥/kWh</div>
    </div>

    {/* Earnings from Grid (computed) */}
    <div className="fieldRow">
      <div className="label">Earnings from Grid:</div>
      <div className="valueRight">{formatMax2(earningsGrid)}</div>
      <div className="unit">¥</div>
    </div>
  </div>
</div>

<div className="editCard">
  <div className="cardTitle">Earnings from Site</div>
  <div className="editCardBody">
    {/* Energy Sold to Site */}
    <div className="fieldRow">
      <div className="label">Energy Sold to Site:</div>
      <Form.Item name="siteKwh" style={{ marginBottom: 0 }}>
        <InputNumber
          className="w100"
          controls={false}
          readOnly
          formatter={(v) => (v !== undefined ? `${v}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',') : '')}
          parser={(v) => (v ? v.replace(/,/g, '') : '')}
        />
      </Form.Item>
      <div className="unit">kWh</div>
    </div>

    {/* Pricing Model */}
    <div className="fieldRow">
      <div className="label">Pricing Model:</div>
      <Form.Item name="priceModel" style={{ marginBottom: 0 }}>
        <Select
          className="w100"
          options={[
            { value: 'FIXED', label: 'Fixed' },
            { value: 'BLOPS', label: 'BLOPS' },
          ]}
        />
      </Form.Item>
      <div className="unit">Fixed or BLOPS</div>
    </div>

    {/* Price / Ratio field depends on Pricing Model */}
    <div className="fieldRow">
      <div className="label">{priceModel === 'BLOPS' ? 'BLOPS Discount Ratio:' : 'Fixed Solar Price (or FIT):'}</div>
      {priceModel === 'BLOPS' ? (
        <Form.Item name="discountKwh" style={{ marginBottom: 0 }}>
          <InputNumber className="w100" controls={false} step={0.01} min={0} max={1} />
        </Form.Item>
      ) : (
        <Form.Item name="sitePrice" style={{ marginBottom: 0 }}>
          <InputNumber className="w100" controls={false} step={0.01} min={0} />
        </Form.Item>
      )}
      <div className="unit">{priceModel === 'BLOPS' ? '' : '¥/kWh'}</div>
    </div>

    {/* Earnings from Site (computed) */}
    <div className="fieldRow">
      <div className="label">Earnings from Site:</div>
      <div className="valueRight">{formatMax2(earningsSite)}</div>
      <div className="unit">¥</div>
    </div>
  </div>
</div>

<div className="editCard">
  <div className="cardTitle">Summary</div>
  <div className="editCardBody">
    {/* Self Utilization */}
    <div className="fieldRow">
      <div className="label">Self Utilization:</div>
      <Form.Item name="selfUtilization" style={{ marginBottom: 0 }}>
        <InputNumber
          className="w100"
          controls={false}
          readOnly
          min={0}
          step={0.01}
          precision={2}
        />
      </Form.Item>
      <div className="unit">%</div>
    </div>

    {/* Utilization Hours */}
    <div className="fieldRow">
      <div className="label">Utilization Hours:</div>
      <Form.Item name="utilizationHours" style={{ marginBottom: 0 }}>
        <InputNumber
          className="w100"
          controls={false}
          readOnly
          min={0}
          step={0.01}
          precision={2}
        />
      </Form.Item>
      <div className="unit">hours</div>
    </div>

    {/* Total Earnings (computed) */}
    <div className="fieldRow">
      <div className="label">Total Earnings:</div>
      <div className="valueRight">{formatMax2(totalEarnings)}</div>
      <div className="unit">¥</div>
    </div>
  </div>
</div>

          </div>

          {/* RIGHT COLUMN */}
          <div className="rightCol">
            <div className="blopsCard">
              <div className="cardTitle">BLOPS Schedule</div>
              <FinanceBlopsSchedule form={formInstance} />
            </div>

            <div className="blopsCard">
              <div className="cardTitle">BLOPS Pricing Calculations</div>
              <Form.List name="blops">
                {(fields) => (
                  <>
                    <div className="blopsTable">
                      <div className="blopsHead blopsGrid">
                        <span className="th tier"></span>
                        <span className="th"><span className="th-line">Unit Price</span><span className="th-sub">¥/kWh</span></span>
                        <span className="th"><span className="th-line">Usage</span><span className="th-sub">kWh</span></span>
                        <span className="th gridPrice"><span className="th-line">Grid Price</span><span className="th-sub">¥</span></span>
                      </div>
                      {fields.map((field) => {
                        const rawKey = formInstance.getFieldValue(['blops', field.name, 'tier']);
                        const normKey = toCanonicalTier(rawKey);
                        const meta = getTierMeta(normKey);
                        const unitPrice = formInstance.getFieldValue(['blops', field.name, 'unitPrice']) || 0;
                        const usage = formInstance.getFieldValue(['blops', field.name, 'usage']) || 0;
                        const gridPrice = unitPrice * usage;
                        return (
                          <div className="blopsRow blopsGrid" key={field.key}>
                            <div className="tierCell">
                              <span
                                className="tierSwatch"
                                style={{ background: meta.background, border: `2px solid ${meta.border}` }}
                              />
                              <span className="tierLabel">{meta.label} <span className="tierSuffix">（{meta.labelSuffix}）</span></span>
                            </div>

                            <Form.Item name={[field.name, 'unitPrice']} className="cellInput" noStyle>
                              <InputNumber
                                className="w100"
                                controls={false}
                                min={0}
                                step={1}
                                formatter={formatNumberWithCommas}
                                parser={(v) => (v ? v.replace(/,/g, '') : '')}
                              />
                            </Form.Item>

                            <Form.Item name={[field.name, 'usage']} className="cellInput" noStyle>
                              <InputNumber
                                className="w100"
                                controls={false}
                                min={0}
                                step={1}
                                formatter={formatNumberWithCommas}
                                parser={(v) => (v ? v.replace(/,/g, '') : '')}
                              />
                            </Form.Item>

                            <div className="gridPrice cellDisplay">{usage ? formatCurrency(gridPrice) : '–'}</div>
                          </div>
                        );
                      })}
                    </div>
                    <div className="totalsBar blopsGrid">
                      <span className="totalsLabel">Total</span>
                      <span />
                      <span className="totalsUsage underline">{formatNumberWithCommas(totalBlopsUsage)}</span>
                      <span className="totalsValue underline">{formatCurrency(totalBlopsPrice)}</span>
                    </div>
                  </>
                )}
              </Form.List>
            </div>

            <div className="modalFooter">
              <Button type="primary" size="large" className="submitButton" onClick={handleSubmit}>
                Submit
              </Button>
              <div className="backLink" onClick={onClose}>
                Back
              </div>
            </div>
          </div>
        </div>
      </Form>
    </Modal>
  );
};

export default FinanceEditModal;